/* WOM Reviews API Manager - Global Utility Script */

// Brand colors CSS variables
const brandColorsCSS = `
  :root {
    --brand-yellow: #F8D214;
    --brand-blue: #233988;
  }
`;

// Inject brand colors if not already injected
if (!document.getElementById('wom-brand-colors')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'wom-brand-colors';
  styleElement.textContent = brandColorsCSS;
  document.head.appendChild(styleElement);
}

// Singleton API Manager Class
class ReviewsAPIManager {
  constructor() {
    // Prevent multiple instances
    if (window.reviewsAPIManager) {
      return window.reviewsAPIManager;
    }

    const cfg = window.vesselsWOMConfig || window.clientWOMConfig || {};
    const companyId = cfg.companyId || 'boldfit';
    const fallbackSlug = cfg.fixedProductSlug || cfg.defaultProductSlug || 'boldfit-demo-product';

    if (cfg.reviewsApiUrl) {
      this.apiEndpoint = String(cfg.reviewsApiUrl).split('?')[0];
      this.womReviewsId = cfg.fixedProductSlug || cfg.defaultProductSlug || fallbackSlug;
    } else if (typeof cfg.getProductHandle === 'function') {
      this.womReviewsId = cfg.getProductHandle() || fallbackSlug;
      this.apiEndpoint = `https://api.thewordofmouth.in/api/reviews/${companyId}/${this.womReviewsId}`;
    } else {
      this.womReviewsId = this.getProductTitle();
      if (typeof this.womReviewsId === 'string') {
        let filtered = this.womReviewsId.split(/\s*[-–]\s*/)[0].trim();
        filtered = filtered.replace(/[^\w]+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '').toLowerCase();
        this.womReviewsId = filtered;
      } else {
        this.womReviewsId = fallbackSlug;
      }
      this.apiEndpoint = `https://api.thewordofmouth.in/api/reviews/${companyId}/${this.womReviewsId}`;
    }
    this.cache = new Map();
    this.loading = false;
    this.fetchPromise = null;
    this.initialized = false;

    // Store singleton instance
    window.reviewsAPIManager = this;
  }

  // Get product handle from URL or product object
  getProductHandle() {
    // Try to get from Shopify Analytics
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      const analyticsHandle = window.ShopifyAnalytics.meta.product.handle;
      if (analyticsHandle && analyticsHandle !== 'undefined' && analyticsHandle.trim()) {
        return analyticsHandle;
      }
    }

    // Fallback to URL parsing
    const pathname = window.location.pathname;
    const parts = pathname.split('/');
    const productIndex = parts.indexOf('products');

    if (productIndex !== -1 && productIndex + 1 < parts.length) {
      const handle = parts[productIndex + 1].split('?')[0]; // Remove query parameters
      return handle;
    }

    return 'boldfit-demo-product';
  }

  // Get product title/heading
  getProductTitle() {
    // Try to get from Shopify Analytics
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      const analyticsTitle = window.ShopifyAnalytics.meta.product.title;
      if (analyticsTitle && analyticsTitle !== 'undefined' && analyticsTitle.trim()) {
        return analyticsTitle;
      }
    }

    // Try to get from page title
    const pageTitle = document.title;
    if (pageTitle) {
      // Remove common suffixes like " | Store Name"
      const cleanTitle = pageTitle.split('|')[0].trim();
      return cleanTitle;
    }

    // Try to get product name from URL (for product pages)
    const currentUrl = window.location.pathname;
    if (currentUrl.includes('/products/')) {
      const productHandle = currentUrl.split('/products/')[1];
      if (productHandle) {
        // Convert handle to readable name (replace hyphens with spaces, capitalize)
        const productName = productHandle
          .split('?')[0] // Remove query parameters
          .split('&')[0] // Remove additional parameters
          .replace(/-/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase());
        return productName;
      }
    }

    // Try to get from h1 heading - let's try multiple selectors
    const selectors = [
      'h1.product__title',
      'h1[itemprop="name"]', 
      '.product-single__title',
      'h1',
      '.product-title',
      '.product__heading',
      '[data-product-title]',
      '.product-info h1',
      '.product-details h1'
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element && element.textContent.trim()) {
        const h1Text = element.textContent.trim();
        return h1Text;
      }
    }

    // Try to get from any element with product-related classes
    const productElements = document.querySelectorAll('[class*="product"][class*="title"], [class*="product"][class*="name"], [class*="product"][class*="heading"]');
    for (const element of productElements) {
      if (element.textContent.trim()) {
        const text = element.textContent.trim();
        return text;
      }
    }

    // Default fallback
    return 'Product';
  }

  // Build API URL
  getAPIUrl() {
    return this.apiEndpoint;
  }

  // Fetch reviews data
  async fetchReviews() {
    const apiUrl = this.getAPIUrl();

    // Check cache first
    if (this.cache.has(apiUrl)) {
      return this.cache.get(apiUrl);
    }

    // If there's already an ongoing fetch, return that promise
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Create and store the fetch promise
    this.fetchPromise = this._performFetch(apiUrl);

    try {
      const result = await this.fetchPromise;
      return result;
    } finally {
      this.fetchPromise = null;
    }
  }

  // Internal method to perform the actual fetch
  async _performFetch(apiUrl) {
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          'Origin': window.location.origin,
          'Referer': window.location.href
        },
        credentials: 'omit',
        mode: 'cors'
      });

      if (!response.ok) {
        // If 404, it means no reviews exist yet - return empty data to show "Write First Review"
        if (response.status === 404) {
          const emptyData = {
            reviews: [],
            overall_rating: 0,
            total_reviews: 0,
            company_info: {},
            keywords: [],
            product_id: this.womReviewsId
          };
          window.globalReviewData = emptyData;
          this.cache.set(apiUrl, emptyData);
          return emptyData;
        }

        // For other errors, return null to hide widgets
        return null;
      }

      const data = await response.json();

      // Random title variations for repetitive API titles
      const randomTitleVariations = [
        "Excellent!", "Perfect!", "Amazing!", "Brilliant!", "Outstanding!",
        "Love it", "Super tasty", "Best purchase", "Really impressed", "Excellent taste",
        "Worth it", "Great nutrition", "Will repurchase", "Perfect taste", "Amazing experience",
        "Highly recommend this", "Outstanding quality product", "Fantastic product overall",
        "Superb quality taste", "Exceptional product quality"
      ];

      // Function to get varied title if original is generic
      function getVariedTitle(originalTitle, index) {
        const genericTitles = ['nice taste', 'good', 'great', 'excellent', 'nice', 'good taste'];

        if (!originalTitle || genericTitles.some(generic =>
          originalTitle.toLowerCase().includes(generic.toLowerCase()))) {
          return randomTitleVariations[index % randomTitleVariations.length];
        }
        return originalTitle;
      }

      // Transform the Word of Mouth API response
      const transformedData = {
        reviews: (data.reviews || []).map((review, index) => ({
          ...review,
          title: getVariedTitle(review.title, index)
        })),
        overall_rating: data.overall_rating || data.overallRating || data.average_rating || data.averageRating || 0,
        total_reviews: data.total_reviews || data.totalReviews || data.total_count || data.totalCount || (data.reviews ? data.reviews.length : 0),
        star_breakdown: data.star_breakdown || data.starBreakdown || null,
        company_info: data.company_info || data.companyInfo || {},
        keywords: data.keywords || [],
        product_id: this.womReviewsId
      };

      // Set global variable FIRST
      window.globalReviewData = transformedData;

      // Cache the response
      this.cache.set(apiUrl, transformedData);

      return transformedData;

    } catch (error) {
      // Return null to indicate failure - no widgets should render
      return null;
    }
  }

  // Update all review components
  async updateAllComponents() {
    try {
      let data = await this.fetchReviews();

      // If data is null (API failed), don't render any widgets
      if (!data) {
        window.dispatchEvent(new CustomEvent('reviewsDataLoaded', {
          detail: null
        }));
        return;
      }

      // Update all components that exist on the page
      if (window.updateSocialProofBanner) {
        window.updateSocialProofBanner(data);
      }

      if (window.updateProductReviews) {
        window.updateProductReviews(data);
      }

      if (window.updateReviewsBreakdown) {
        window.updateReviewsBreakdown(data);
      }

      if (window.updateReviewsBreakdownDetailed) {
        window.updateReviewsBreakdownDetailed(data);
      }

      if (window.updateReviewCardsFromCache) {
        window.updateReviewCardsFromCache(data.reviews || []);
      }

      // Dispatch custom event for other components
      window.dispatchEvent(new CustomEvent('reviewsDataLoaded', {
        detail: data
      }));

    } catch (error) {
      // Silent fail
    }
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
  }

  // Refresh data
  async refresh() {
    this.clearCache();
    return await this.updateAllComponents();
  }
}

// Initialize the API manager (singleton)
if (!window.reviewsAPIManager) {
  window.reviewsAPIManager = new ReviewsAPIManager();
}

// Initialize global variable immediately
window.globalReviewData = {
  overall_rating: null,
  total_reviews: null,
  reviews: []
};

/**
 * Percentages per star (1–5) from API star_breakdown, or derived from loaded reviews only.
 */
window.womGetStarBreakdownPercents = function (data) {
  if (!data) return null;
  const bd = data.star_breakdown;
  if (bd && typeof bd === 'object') {
    const out = {
      5: Number(bd['5_star']) || 0,
      4: Number(bd['4_star']) || 0,
      3: Number(bd['3_star']) || 0,
      2: Number(bd['2_star']) || 0,
      1: Number(bd['1_star']) || 0
    };
    if (out[5] + out[4] + out[3] + out[2] + out[1] > 0) {
      return out;
    }
  }
  const rev = data.reviews;
  if (Array.isArray(rev) && rev.length > 0) {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    for (let i = 0; i < rev.length; i++) {
      const rt = parseInt(rev[i].rating, 10);
      if (rt >= 1 && rt <= 5) counts[rt]++;
    }
    const n = rev.length;
    return {
      5: Math.round((counts[5] / n) * 100),
      4: Math.round((counts[4] / n) * 100),
      3: Math.round((counts[3] / n) * 100),
      2: Math.round((counts[2] / n) * 100),
      1: Math.round((counts[1] / n) * 100)
    };
  }
  return null;
};

// Start API call IMMEDIATELY (don't wait for DOMContentLoaded)
window.reviewsAPIManager.fetchReviews().then(data => {
  if (data) {
    window.globalReviewData = data;
  }
});

// Update components when DOM is ready (data should already be cached)
document.addEventListener('DOMContentLoaded', function() {
  if (window.reviewsAPIManager.initialized) {
    return;
  }

  // Mark as initialized
  window.reviewsAPIManager.initialized = true;

  // This will use cached data from the early fetch
  window.reviewsAPIManager.updateAllComponents();
});

// Handle back/forward navigation
window.addEventListener('pageshow', function(event) {
  if (event.persisted && window.reviewsAPIManager) {
    window.reviewsAPIManager.updateAllComponents();
  }
});

// Function to update reviews section banner
window.updateReviewsSectionBanner = function(data) {
  const starsElement = document.getElementById('social-proof-stars-reviews');
  const reviewsCountElement = document.getElementById('social-proof-reviews-count-reviews');

  if (!starsElement || !reviewsCountElement) {
    return;
  }

  if (data && data.overall_rating) {
    starsElement.innerHTML = window.renderWomStars
      ? window.renderWomStars(data.overall_rating, 17)
      : '';
  }

  if (data && data.total_reviews) {
    reviewsCountElement.textContent = `(${String(Number(data.total_reviews) || 0)})`;
  }

  const banner = document.getElementById('social-proof-banner-in-reviews');
  if (banner) {
    banner.style.display = 'flex';
  }
};

// Inject social proof banner snippet into reviews section
document.addEventListener('DOMContentLoaded', function() {
  setTimeout(() => {
    // Find the Reviews collapsible content by looking for the placeholder text
    const paragraphs = document.querySelectorAll('p');
    let placeholderElement = null;
    
    paragraphs.forEach(p => {
      if (p.textContent && p.textContent.includes('WOM_REVIEWS_PLACEHOLDER')) {
        placeholderElement = p;
      }
    });
    
    if (placeholderElement) {
      // Create a new container for the social proof banner
      const bannerContainer = document.createElement('div');
      bannerContainer.innerHTML = `
        <div id="social-proof-banner-in-reviews" class="stats-bar" style="display: block;">
          <div class="stats-content">
            <span id="social-proof-stars-reviews" class="stars"></span>
            <span id="social-proof-reviews-count-reviews" class="stat-number"></span>
          </div>
        </div>
      `;
      
      // Replace the placeholder with the new banner
      placeholderElement.replaceWith(bannerContainer.firstElementChild);
      
      // Update the banner with data
      if (window.globalReviewData) {
        window.updateReviewsSectionBanner(window.globalReviewData);
      }
    }
  }, 1000);
  
  // Listen for data loaded event
  window.addEventListener('reviewsDataLoaded', function(event) {
    if (event.detail) {
      setTimeout(() => {
        window.updateReviewsSectionBanner(event.detail);
      }, 200);
    }
  });
});

// Utility functions for other scripts
window.refreshReviews = function() {
  return window.reviewsAPIManager ? window.reviewsAPIManager.refresh() : Promise.resolve();
};

window.getReviewsData = function() {
  return window.reviewsAPIManager ? window.reviewsAPIManager.fetchReviews() : Promise.resolve({ reviews: [] });
};

window.debugReviews = function() {
  return {
    apiManager: window.reviewsAPIManager,
    cache: window.reviewsAPIManager?.cache,
    productHandle: window.reviewsAPIManager?.getProductHandle(),
    apiUrl: window.reviewsAPIManager?.getAPIUrl()
  };
};
